<template>
  <div
    :id="field.id"
    v-tooltip="tooltip"
    :class="['field-container', { active }]"
    :style="{ top, left, 'font-size': fontSize, height, width, 'min-width': minWidth }"
  >
    <div
      :class="[
        'field',
        fieldType,
        fieldSource,
        pageType,
        { disabled, completed, invalid, focus, signed: isSigned },
      ]"
      :tabIndex="tabIndex"
      @keydown.space="setPressSpace($event, true)"
      @keyup.space="setPressSpace($event, false)"
      @focus="setFocus(true)"
      @blur="setFocus(false)"
    >
      <CheckboxField
        v-if="field.type === FieldType.Check"
        :field="field"
        :disabled="disabled"
        :focused="focus"
        :press-space="pressSpace"
        @update="handleUpdate"
      />
      <DropdownField
        v-if="field.type === FieldType.Dropdown"
        :field="field"
        :disabled="disabled"
        :completed="completed"
        @update="handleUpdate"
        @toggle-dropdown="handleDropdownToggle"
      />
      <TextField
        v-if="isTextBasedField"
        :field="field"
        :disabled="disabled"
        :completed="completed"
        :field-focused="focus"
        :is-connected-to-current-recipient="isConnectedToCurrentRecipient"
        @update="handleUpdate"
        @focus="setFocus(true)"
        @blur="setFocus(false)"
      />
      <MergeField v-if="field.type === FieldType.Merge" :field="field" />
      <InitialsField
        v-if="field.type === FieldType.Initials"
        :initials="initials"
        :verified-initials="field.verifiedInitials"
        :disabled="disabled"
        :full-name="fullName || ''"
        :completed="completed"
        @click="handleInitialsClick"
      />
      <LinkField v-if="field.type === FieldType.Link" @click="handleClickLink" />
      <SignatureField
        v-if="field.type === FieldType.Signature"
        :field="field"
        :disabled="disabled || signatureDisabled"
        :full-name="fullName || ''"
        :required-steps-left="requiredStepsLeft"
        @click="handleSignature"
      />
    </div>
    <small v-if="invalid" class="field-feedback" :style="{ top: height }">
      {{ errorMessage }}
    </small>
  </div>
</template>
<script lang="ts">
import { Placement as TooltipPlacement } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import type { PropType } from 'vue';
import { defineComponent, watch, computed, ref } from 'vue';
import SignatureField from './SignatureField.vue';
import InitialsField from './InitialsField.vue';
import CheckboxField from './CheckboxField.vue';
import DropdownField from './DropdownField.vue';
import MergeField from './MergeField.vue';
import LinkField from './LinkField.vue';
import TextField from './TextField.vue';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import { FieldHelper } from '@getaccept/lib-shared/src/fields/helpers/field.helper';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { Field as IField } from '@getaccept/lib-shared/src/fields/types/field';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import { FieldType } from '@getaccept/lib-shared/src/fields/types/enums/field-type';

export default defineComponent({
  components: {
    InitialsField,
    CheckboxField,
    MergeField,
    LinkField,
    TextField,
    DropdownField,
    SignatureField,
  },
  props: {
    field: {
      type: Object as PropType<IField>,
      required: true,
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: () => ({}) as Recipient,
    },
    allRecipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      default: () => ({}),
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
    },
    requiredStepsLeft: {
      type: Boolean,
    },
    isSigned: {
      type: Boolean,
    },
    initials: {
      type: String,
      default: '',
    },
    verifiedInitialFieldIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  emits: ['input', 'update', 'click-initials-field', 'click-sign-field', 'click-link-field'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const active = ref(false);
    const focus = ref(false);
    const pressSpace = ref(false);

    const watchFocusedRequiredId = (focusedRequiredId: string) => {
      if (props.field.id === focusedRequiredId) {
        setFocus(true);
      } else {
        setFocus(false);
      }
    };

    watch(() => props.focusedRequiredId, watchFocusedRequiredId);

    const errorMessage = computed(
      () => props.field.validationWarning || t('invalid-field-value-entered')
    );

    const tabIndex = computed(() => {
      if (props.isDisabled || !FieldHelper.isTabbableField(props.field.type)) {
        return undefined;
      }
      return 0;
    });

    const fontSize = computed(() => `${(props.field.position.height * 0.6).toFixed(1)}px`);

    const top = computed(() => `${props.field.position.top}px`);

    const left = computed(() => `${props.field.position.left}px`);

    const height = computed(() => {
      let fieldHeight = `${props.field.position.height}px`;
      if (!shouldGrow.value || !props.field.value) {
        return fieldHeight;
      }
      if (!isConnectedToCurrentRecipient.value) {
        fieldHeight = 'auto';
      }
      return fieldHeight;
    });

    const width = computed(() => {
      if (props.field.type === FieldType.Initials) {
        return 'auto';
      }
      return `${props.field.position.width}px`;
    });

    const minWidth = computed(() => {
      if (props.field.type === FieldType.Initials) {
        return `${props.field.position.width}px`;
      }
      return 'auto';
    });

    const fieldType = computed(() => `${props.field.type}-field`);

    const fieldSource = computed(() =>
      props.field.source === FieldSource.Sender ? 'sender-field' : 'recipient-field'
    );

    const pageType = computed(() => 'signing-page-field');

    const completed = computed(() =>
      FieldHelper.fieldCompleted(props.field, props.verifiedInitialFieldIds)
    );

    const disabled = computed(() => {
      if (props.isDisabled) {
        return true;
      }

      if (
        props.field.source === FieldSource.Sender ||
        (props.recipient &&
          (props.recipient.status === RecipientStatus.Signed ||
            props.recipient.status === RecipientStatus.Rejected))
      ) {
        return true;
      }
      return (
        FieldHelper.isInputField(props.field.type) && props.field.recipientId !== props.recipient.id
      );
    });

    const signatureDisabled = computed(
      () => props.field.type === FieldType.Signature && !disabled.value && props.requiredStepsLeft
    );

    const tooltip = computed(() => {
      if (showTooltip.value) {
        return {
          content: tooltipContent.value,
          distance: 10,
          placement: TooltipPlacement.Top,
        };
      }
      return null;
    });

    const showTooltip = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return false;
      }
      return FieldHelper.showFieldTooltip(
        props.field.type,
        disabled.value,
        props.requiredStepsLeft
      );
    });

    const invalid = computed(() => {
      if (props.field.type !== FieldType.Formatted) {
        return false;
      }
      return props.field.value && !new RegExp(props.field.validationRegex).test(props.field.value);
    });

    const tooltipContent = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return t('field-assigned-to-you');
      }
      if (signatureDisabled.value) {
        return t('required-steps-remaining-before-sign');
      }
      return `${t('field-assigned-to')} ${fullName.value}`;
    });

    const fullName = computed(() => {
      switch (props.field.source) {
        case FieldSource.Recipient:
          return !!fieldRecipient.value && fieldRecipient.value.fullName;
        case FieldSource.Sender:
          return !!props.sender && props.sender.fullName;
        default:
          return null;
      }
    });

    const fieldRecipient = computed(() => {
      if (!props.field || !props.field.recipientId) {
        return null;
      }
      return props.allRecipients.find(
        (recipient: Recipient) => recipient.id === props.field.recipientId
      );
    });

    const shouldGrow = computed(() =>
      [FieldType.Text, FieldType.Formatted, FieldType.Merge].includes(props.field.type)
    );

    const isTextBasedField = computed(
      () => props.field.type === FieldType.Text || props.field.type === FieldType.Formatted
    );

    const isConnectedToCurrentRecipient = computed(
      () => props.recipient?.id === props.field.recipientId
    );

    const handleInput = (value: string) => {
      emit('input', value);
    };

    const handleDropdownToggle = (isOpen: boolean) => {
      active.value = isOpen;
    };

    const handleUpdate = (value: string) => {
      emit('update', { fieldId: props.field.id, value });
    };

    const setFocus = (hasFocus: boolean) => {
      if (disabled.value) {
        return;
      }
      focus.value = hasFocus;
    };

    const setPressSpace = (event: KeyboardEvent, press: boolean) => {
      if (props.field.type === FieldType.Check) {
        event.preventDefault();
        pressSpace.value = press;
      }
    };

    const handleInitialsClick = () => {
      if (disabled.value) {
        return;
      }
      emit('click-initials-field', props.field.id);
    };

    const handleClickLink = () => {
      if (props.field.value) {
        window.open(
          props.field.value.includes('http') ? props.field.value : `//${props.field.value}`
        );
      }
      emit('click-link-field', props.field.id);
    };

    const handleSignature = () => {
      emit('click-sign-field');
    };

    return {
      FieldType,
      FieldSource,
      RecipientStatus,
      active,
      completed,
      disabled,
      height,
      fieldSource,
      fieldRecipient,
      fieldType,
      focus,
      fullName,
      handleDropdownToggle,
      handleInitialsClick,
      handleClickLink,
      handleInput,
      handleSignature,
      handleUpdate,
      invalid,
      isTextBasedField,
      isConnectedToCurrentRecipient,
      minWidth,
      pageType,
      pressSpace,
      setFocus,
      setPressSpace,
      shouldGrow,
      signatureDisabled,
      tooltip,
      tooltipContent,
      width,
      errorMessage,
      tabIndex,
      fontSize,
      top,
      left,
      watchFocusedRequiredId,
    };
  },
});
</script>

<style lang="scss" scoped>
.field-container {
  position: absolute;

  .field {
    height: 100%;
    width: 100%;
    border-radius: 0.25em;
    border: 1px solid var(--base-blue);
    display: flex;
    align-items: center;
    transition: border-color 0.1s ease-in-out;
    padding-left: 0.25em;
    box-sizing: border-box;
    font-weight: normal;

    &.signing-page-field {
      &.completed:not(.disabled, .merge-field, .link-field) {
        border-color: var(--success-dark);
        background-color: transparent;
      }

      &.invalid:not(.disabled, .sender-field) {
        border-color: var(--error-dark);
      }

      &:hover:not(.disabled, .sender-field) {
        border-color: var(--brand-orange);
      }

      &.focus:not(.disabled, .sender-field) {
        outline: none;
        border: 1px solid var(--brand-orange);

        &.invalid {
          border-color: var(--error-dark);
        }

        &.completed {
          border-color: var(--success-dark);
        }
      }

      &.disabled:not(.merge-field, .sender-field) {
        background-color: var(--field-gray-background);
        border-color: var(--text-gray);
      }

      &.sender-field,
      &.merge-field,
      &.link-field,
      &.signed {
        border-color: transparent;
        background-color: transparent;
        color: var(--base-blue);

        &.check-field,
        &.signature-field {
          border-color: var(--text-gray);
        }
      }
    }

    &.check-field {
      padding-left: 0;
    }

    &.link-field {
      border-color: transparent;
      background: transparent;
    }
  }

  .field-feedback {
    color: var(--error-dark);
    background: var(--white);
    position: absolute;
    margin-top: var(--spacing-50);
    line-height: var(--label-small-line-height);
    left: var(--spacing-25);
  }

  &.active {
    z-index: $z1;
  }
}
</style>
